<template>
  <div>
    <label v-html="addNbsp(getLocaleText(inputData.label))" />
    <v-radio-group
      v-model="$v.inputValue.$model"
      :error="$v.inputValue.$error"
      @change="updateValue"
    >
      <v-radio
        v-for="opt in normalizedValues"
        :key="opt.id"
        :value="opt.id"
        :class="{ 'mb-2 mb-md-4': true }"
      >
        <template #label>
          <span>{{ getLocaleText(opt.display) }}</span>
        </template>
      </v-radio>
    </v-radio-group>
  </div>
</template>

<script>
import formGtm from '@/mixins/formGtm';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'UlRadio',
  mixins: [formGtm],
  props: {
    inputData: { type: Object, required: true },
    stepId: { type: [Number, String], required: true },
    formName: { type: String, required: false, default: null },
    value: { required: false, default: null }
  },
  data() {
    return {
      inputValue: this.value
    };
  },
  validations() {
    if (this.inputData.required) {
      return { inputValue: { required } };
    }
    return { inputValue: {} };
  },
  computed: {
    responses() {
      const getter = this.$store.getters['dynamicForms/responses'];
      return typeof getter === 'object' ? getter : {};
    },
    legacyFormName() {
      if (this.formName) {
        return this.formName;
      }
      const id = this.inputData?.id || '';
      const parts = String(id).split('.');
      return parts.length ? parts[0] : 'form';
    },
    normalizedValues() {
      const raw = Array.isArray(this.inputData.values) ? this.inputData.values : [];
      return raw.map(val => {
        if (typeof val === 'string' || typeof val === 'number') {
          const id = String(val);
          return { id, display: this.optionKeyFor(id) };
        }
        const id = val?.id !== null ? String(val.id) : String(val?.value ?? '');
        const display = typeof val?.value === 'string' ? val.value : this.optionKeyFor(id);
        return { id, display };
      });
    }
  },
  watch: {
    value(val) {
      const str = val === null ? null : String(val);
      if (str !== (this.inputValue === null ? null : String(this.inputValue))) {
        this.inputValue = val;
        if (this.$v) {
          this.$v.$touch();
        }
        this.$emit('valid-change', !this.$v.$invalid);
      }
    },
    normalizedValues: {
      handler(values) {
        const labels = {};
        values.forEach(opt => {
          if (!opt || opt.id === undefined || opt.id === null) {
            return;
          }
          const localized =
            (typeof this.getLocaleText === 'function' && this.getLocaleText(opt.display)) ||
            opt.display ||
            '';
          if (!localized) {
            return;
          }
          labels[String(opt.id)] = localized;
        });
        this.$emit('value-labels', labels);
      },
      immediate: true
    }
  },
  created() {
    this.$eventHub.$on(`validateInput_${this.inputData.id}_${this.stepId}`, () => this.validate());
    this.$eventHub.$on(`resetValidationInput_${this.inputData.id}_${this.stepId}`, () =>
      this.reset()
    );

    const prev = this.responses?.[this.legacyFormName]?.[this.inputData.id];
    if (prev !== undefined && prev !== null) {
      this.inputValue = prev;
    }

    if (this.inputValue !== undefined && this.inputValue !== null && this.inputValue !== '') {
      this.updateValue(false);
    }
    this.reset();
  },
  destroyed() {
    this.$eventHub.$off(`validateInput_${this.inputData.id}_${this.stepId}`);
    this.$eventHub.$off(`resetValidationInput_${this.inputData.id}_${this.stepId}`);
  },
  methods: {
    optionKeyFor(id) {
      const base = `${this.legacyFormName}.${this.inputData.name || 'field'}`;
      return `${base}.${id}`;
    },
    validate() {
      this.$v.$touch();
      if (this.$store._mutations['dynamicForms/updateValidation']) {
        this.$store.commit('dynamicForms/updateValidation', {
          id: this.inputData.id,
          value: !this.$v.$invalid,
          formName: this.legacyFormName
        });
      }
    },
    reset() {
      this.$v.$reset();
    },
    updateValue(emit = true) {
      this.validate();
      if (this.$store._actions['dynamicForms/updateResponse']) {
        this.$store.dispatch('dynamicForms/updateResponse', {
          id: this.inputData.id,
          value: this.$v.inputValue.$model === null ? null : this.$v.inputValue.$model,
          formName: this.legacyFormName
        });
      }
      if (emit) {
        this.$emit(
          'update:value',
          this.$v.inputValue.$model === null ? null : this.$v.inputValue.$model
        );
        this.$emit('valid-change', !this.$v.$invalid);
      }
    }
  }
};
</script>
